import { NgTemplateOutlet } from '@angular/common';
import {
  afterRenderEffect,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  DestroyRef,
  effect,
  type ElementRef,
  inject,
  input,
  output,
  Renderer2,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { type ControlValueAccessor } from '@angular/forms';

import {
  NgpSelect,
  NgpSelectDropdown,
  NgpSelectOption,
  NgpSelectPortal,
} from 'ng-primitives/select';
import { provideValueAccessor } from 'ng-primitives/utils';
import { type Observable } from 'rxjs';
import { twMerge } from 'tailwind-merge';

import { I18nPipe } from '@core/i18n';

import { ChipComponent, chipCva, DismissableDirective } from '@shared/chip';
import { IconComponent } from '@shared/icon';
import { TooltipDirective } from '@shared/tooltip';

import {
  dropdownCva,
  emptyCva,
  optionCva,
  optionWrapperCva,
  placeholderCva,
  selectCva,
  type SelectSize,
} from './select.cva';
import { SelectOptionDirective, SelectValueDirective } from './select.directive';
import { CHEVRON_WIDTH, GAP, OVERFLOW_WIDTH } from './select.types';

@Component({
  selector: 'ml-select',
  imports: [
    NgpSelect,
    NgpSelectDropdown,
    NgpSelectOption,
    NgpSelectPortal,
    NgTemplateOutlet,
    IconComponent,
    ChipComponent,
    TooltipDirective,
    I18nPipe,
    DismissableDirective,
  ],
  providers: [provideValueAccessor(SelectComponent)],
  host: {
    '[class]': 'hostClass()',
  },
  templateUrl: './select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectComponent<T> implements ControlValueAccessor {
  readonly placeholder = input<string>('select.placeholder');
  readonly emptyText = input<string>('common.select.emptyText');
  readonly errorText = input<string>('select.error');
  readonly multiple = input(false, { transform: booleanAttribute });
  readonly search = input(false, { transform: booleanAttribute });
  readonly async = input(false, { transform: booleanAttribute });
  readonly showClearBtn = input<boolean>(true);
  readonly required = input(false, { transform: booleanAttribute });
  readonly options = input<readonly T[]>([]);
  readonly optionIdKey = input<keyof T | null>(null);
  readonly displayWith = input<(value: T) => string>();
  readonly displayKey = input<keyof T | null>(null);
  readonly disabledKey = input<keyof T | null>(null);
  readonly extraClass = input<string>('');
  readonly displayTitle = input<keyof T | null>(null);
  readonly size = input<SelectSize>('md');

  //internal implementation
  readonly fetchFn = input<((params: { term: string; page: number }) => Observable<T[]>) | null>(
    null,
  );
  readonly openChange = output<boolean>();

  readonly searchChange = output<string>();

  private readonly selectRef = viewChild.required<ElementRef<HTMLElement>>('select');
  private readonly chipContainerRef = viewChild<ElementRef<HTMLElement>>('chipContainer');

  private readonly measureChipRefs = viewChildren<ElementRef<HTMLElement>>('measureChip');
  protected readonly optionDirective = contentChild(SelectOptionDirective);
  protected readonly valueDirective = contentChild(SelectValueDirective);

  private readonly renderer = inject(Renderer2);
  private readonly destroyRef = inject(DestroyRef);

  private documentClickUnlisten?: () => void;

  private readonly selectWidth = signal(0);
  private readonly containerWidth = signal(0);
  private readonly measuredWidths = signal<number[]>([]);
  private selectObserver?: ResizeObserver;
  private observer?: ResizeObserver;
  protected readonly isOpen = signal(false);

  //VARIABLES TO VALIDATE LOGIC IN COMPONENT
  private readonly searchTerm = signal('');
  private readonly page = signal(1);
  private readonly optionsInternal = signal<T[]>([]);

  protected readonly isLoading = signal<boolean>(false);
  protected readonly error = signal<boolean>(false);

  private readonly hasMore = signal<boolean>(true);
  private reachedBottom = false;
  private onChange: (value: T | T[] | null) => void = () => {
    return;
  };

  private onTouched: () => void = () => {
    return;
  };
  protected readonly value = signal<T | T[] | null>(null);

  protected readonly disabled = signal(false);
  protected readonly optionWrapperClass = optionWrapperCva;

  protected readonly optionClass = optionCva;
  protected readonly emptyClass = emptyCva;
  protected readonly chipClass = chipCva();
  protected readonly placeholderClass = placeholderCva;

  protected readonly selectClass = computed(() => selectCva({ size: this.size() }));
  protected readonly query = computed(() => ({
    term: this.searchTerm(),
    page: this.page(),
  }));

  protected readonly compareWith = computed(() => {
    const k = this.optionIdKey();

    return k ? (a: T, b: T) => a?.[k] === b?.[k] : (a: T, b: T) => a === b;
  });
  protected readonly selectedOptions = computed<T[]>(() => {
    const v = this.value();

    if (v === null || v === undefined) return [];
    if (Array.isArray(v)) return v;

    return [v];
  });
  protected readonly selectedSet = computed(() => {
    const k = this.optionIdKey();
    const selected = this.selectedOptions();

    if (k) {
      const set = new Set<unknown>(selected.map((v) => v[k]));

      return { has: (opt: T) => set.has(opt[k]) };
    }

    const set = new Set<unknown>(selected);

    return { has: (opt: T) => set.has(opt) };
  });
  protected readonly disabledSet = computed(() => {
    const k = this.disabledKey();

    if (!k) return null;

    const idKey = this.optionIdKey();
    const source = this.fetchFn() ? this.optionsInternal() : this.options();
    const disabled = source.filter((o) => Boolean(o[k]));

    return idKey ? new Set<unknown>(disabled.map((o) => o[idKey])) : new Set<unknown>(disabled);
  });
  protected readonly visibleCount = computed(() => {
    const container = this.containerWidth();
    const measured = this.measuredWidths();
    const count = this.selectedOptions().length;

    if (!container || !measured.length) return count;

    const usable = container - CHEVRON_WIDTH;
    let used = 0;

    for (let i = 0; i < measured.length; i++) {
      const width = measured[i] ?? 0;
      const cost = i === 0 ? width : width + GAP;
      const remaining = count - i - 1;
      const overflowCost = remaining > 0 ? OVERFLOW_WIDTH + GAP : 0;
      if (used + cost + overflowCost > usable) return i;

      used += cost;
    }

    return measured.length;
  });
  protected readonly overflow = computed(() => {
    const all = this.selectedOptions();
    const visible = all.slice(0, this.visibleCount());
    const hidden = all.slice(this.visibleCount());

    return {
      visible,
      count: hidden.length,
      tooltip: hidden.map((v) => this.resolveDisplay(v)).join(', '),
    };
  });
  protected readonly hostClass = computed(() => twMerge(this.extraClass()));
  protected readonly dropdownWidth = computed(() => this.selectWidth() || null);
  protected readonly dropdownClass = computed(() => dropdownCva({ withSearch: this.search() }));
  protected readonly groupedOptions = computed(() => {
    const groupKey = this.displayTitle();
    const opts = this.fetchFn() ? this.optionsInternal() : this.options();
    const term = this.searchTerm().toLowerCase().trim();

    const filtered =
      this.search() && term
        ? opts.filter((item) => this.resolveDisplay(item).toLowerCase().includes(term))
        : opts;

    if (!groupKey) {
      return [{ title: null, items: filtered }];
    }

    const groups = new Map<string | null, T[]>();

    for (const item of filtered) {
      const groupValue = item[groupKey];
      const title = groupValue == null ? null : String(groupValue).trim() || null;

      if (!groups.has(title)) {
        groups.set(title, []);
      }

      groups.get(title)!.push(item);
    }

    return Array.from(groups.entries()).map(([title, items]) => ({
      title,
      items,
    }));
  });

  constructor() {
    this.documentClickUnlisten = this.renderer.listen('document', 'click', (event: Event) => {
      this.handleClickOutside(event);
    });

    effect(() => {
      this.searchTerm();
      this.page.set(1);
    });

    effect((onCleanup) => {
      const fn = this.fetchFn();

      if (!fn) return;

      const { term, page } = this.query();

      this.error.set(false);
      this.isLoading.set(true);
      const sub = fn({ term, page }).subscribe({
        next: (res) => {
          this.optionsInternal.update((prev) => (page === 1 ? res : [...prev, ...res]));
          this.hasMore.set(res.length > 0);
          this.isLoading.set(false);
        },
        error: () => {
          this.optionsInternal.set([]);
          this.hasMore.set(false);
          this.isLoading.set(false);
          this.error.set(true);
        },
      });

      onCleanup(() => sub.unsubscribe());
    });

    afterRenderEffect(() => {
      const select = this.selectRef().nativeElement;

      if (!this.selectObserver) {
        this.selectObserver = new ResizeObserver(() => this.updateSelectWidth());

        this.selectObserver.observe(select);
      }

      this.updateSelectWidth();

      if (!this.multiple()) {
        this.observer?.disconnect();
        this.observer = undefined;

        return;
      }

      const chipRefs = this.measureChipRefs();
      const container = this.chipContainerRef()?.nativeElement;

      if (chipRefs.length) {
        this.measuredWidths.set(chipRefs.map((r) => r.nativeElement.getBoundingClientRect().width));
      }

      if (container && !this.observer) {
        this.observer = new ResizeObserver((entries) => {
          this.containerWidth.set(entries[0]?.contentRect.width ?? 0);
        });

        this.observer.observe(container);
        this.containerWidth.set(container.getBoundingClientRect().width);
      }
    });

    this.destroyRef.onDestroy(() => {
      this.documentClickUnlisten?.();
      this.selectObserver?.disconnect();
      this.observer?.disconnect();
    });
  }

  writeValue(value: T | T[] | null): void {
    this.value.set(value ?? null);
  }

  registerOnChange(fn: (value: T | T[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(value: boolean): void {
    this.disabled.set(value);
  }

  handleClickOutside(event: Event): void {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const clickedInsideSelect = this.selectRef().nativeElement.contains(target);
    const clickedInsideDropdown = target.closest('[data-select-dropdown]');

    if (!clickedInsideSelect && !clickedInsideDropdown) {
      this.searchTerm.set('');
    }
  }

  onDropdownScroll(event: Event): void {
    const target = event.target as HTMLElement;

    const scrollPosition = target.scrollTop + target.clientHeight;
    const threshold = target.scrollHeight - 50;

    const isNearBottom = scrollPosition >= threshold;

    if (isNearBottom && !this.reachedBottom && !this.isLoading()) {
      this.reachedBottom = true;

      this.loadMore();
    }
    if (!isNearBottom) {
      this.reachedBottom = false;
    }
  }

  onSearch(event: Event): void {
    const target = event.target;

    if (!(target instanceof HTMLInputElement)) return;

    const { value } = target;

    this.searchTerm.set(value);

    if (this.async()) {
      this.searchChange.emit(value);
    }
  }

  protected optionId(option: T): T[keyof T] | T {
    const k = this.optionIdKey();

    return k ? option[k] : option;
  }

  protected isSelected(option: T): boolean {
    return this.selectedSet().has(option);
  }

  protected resolveDisplay(value: T | T[] | null | undefined): string {
    if (value === null || value === undefined) return '';

    const fn = this.displayWith();
    const k = this.displayKey();

    if (Array.isArray(value)) {
      return value.map((v) => fn?.(v) ?? (k ? String(v[k]) : String(v))).join(', ');
    }
    return fn?.(value) ?? (k ? String(value[k]) : String(value));
  }

  protected resolveDisabled(option: T): boolean {
    const set = this.disabledSet();

    if (!set) return false;

    const k = this.optionIdKey();

    return set.has(k ? option[k] : option);
  }

  protected onValueChange(value: T | T[] | null): void {
    this.value.set(value);
    this.onChange(value);
    if (!value) {
      this.searchTerm.set('');
    }
  }

  protected onBlur(): void {
    this.onTouched();
  }

  protected deselect(option: T): void {
    const k = this.optionIdKey();
    const next = this.selectedOptions().filter((o) => (k ? o[k] !== option[k] : o !== option));

    this.value.set(next);
    this.onChange(next);
  }

  protected onOptionActivated(option: T): void {
    if (!this.multiple() && this.isSelected(option) && !this.required()) {
      // Defer until ngpSelect selection commit completes in the same task
      queueMicrotask(() => this.onValueChange(null));
    }
  }

  protected onOpenChange(open: boolean): void {
    if (open) {
      this.updateSelectWidth();
    }

    this.openChange.emit(open);
    this.isOpen.set(open);

    if (!open) {
      this.onTouched();
      this.searchTerm.set('');
    }
  }

  protected loadMore(): void {
    if (this.isLoading() || !this.hasMore()) return;

    this.page.update((p) => p + 1);
  }

  private updateSelectWidth(): void {
    this.selectWidth.set(this.selectRef().nativeElement.getBoundingClientRect().width);
  }
}
